import React, { useState, useEffect } from 'react';
import { Button, Tabs, Typography, Descriptions, Tag, Space, Spin, Flex, Empty } from 'antd';
import { ArrowLeftOutlined, EditOutlined, PhoneOutlined, CalendarOutlined, CheckCircleOutlined, ClockCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { getData } from '../../actions/apiCall';
import moment from 'moment';
import { constants } from '../../constants/common';
import ProfileCard from '../../components/ContactInfo/ProfileCard';
import SmallProfileCard from '../../components/ContactInfo/SmallProfileCard';
import Timeline from '../Leads/Timeline';
import NotesComponent from '../../components/Forms/NotesComponent';
import Attachments from '../Leads/Attachments';
import TasksDetails from '../Leads/Tasks/TasksDetails';
import CallLogsDetails from '../Leads/CallLogs/CallLogsDetails';
import LeadDetails from '../Leads/LeadDetails';
import ContactDetails from '../Leads/Contacts/ContactDetails';
import AccountDetails from '../Leads/Accounts/AccountDetails';
import DealsDetails from '../Leads/Deals/DealsDetails';
import { useParams, useNavigate } from 'react-router-dom';

const { Title, Text, Link } = Typography;
const { TabPane } = Tabs;

interface ActivityDetailsProps {
    activityId?: string;
    activityType?: 'task' | 'call' | 'meeting';
}

const ActivityDetails: React.FC<ActivityDetailsProps> = ({ activityId: propActivityId, activityType: propActivityType }) => {
    // const { activityId, activityType } = useParams<{ activityId: string; activityType: string }>(); // Commented out if react-router-dom is not available
    // const navigate = useNavigate(); // Commented out if react-router-dom is not available
    const dispatch = useDispatch();

    // Get activityId and activityType from URL params or props
    const urlParams = new URLSearchParams(window.location.search);
    const urlPath = window.location.pathname;
    const pathParts = urlPath.split('/').filter(Boolean);

    // Try to extract from URL path: /activities/{type}/{id}
    let urlActivityType: string | undefined;
    let urlActivityId: string | undefined;

    const activitiesIndex = pathParts.indexOf('activities');
    if (activitiesIndex !== -1 && pathParts.length > activitiesIndex + 2) {
        urlActivityType = pathParts[activitiesIndex + 1];
        urlActivityId = pathParts[activitiesIndex + 2];
    }

    // Fallback navigation function
    const navigate = (path: string | number) => {
        if (typeof path === 'string') {
            window.location.href = path;
        } else {
            window.history.back();
        }
    };

    const finalActivityId = propActivityId || urlActivityId || urlParams.get('activityId') || '';
    // Normalize activity type: 'call-schedule' and 'call-log' both map to 'call' for rendering
    const rawActivityType = propActivityType || urlActivityType || urlParams.get('activityType') || 'task';
    const finalActivityType = (rawActivityType === 'call-schedule' || rawActivityType === 'call-log' ? 'call' : rawActivityType) as 'task' | 'call' | 'meeting';
    const actualActivityType = rawActivityType as 'task' | 'call' | 'call-schedule' | 'call-log' | 'meeting';

    const [loading, setLoading] = useState(true);
    const [activity, setActivity] = useState<any>(null);
    const [relatedRecord, setRelatedRecord] = useState<any>(null);
    const [activeTab, setActiveTab] = useState<'overview' | 'timeline'>('overview');

    useEffect(() => {
        if (finalActivityId && finalActivityType) {
            fetchActivityDetails();
        }
    }, [finalActivityId, finalActivityType]);

    const fetchActivityDetails = async () => {
        try {
            setLoading(true);
            let apiUrl = '';

            // Handle different activity types including call-schedule and call-log
            if (actualActivityType === 'task') {
                apiUrl = `/tasks-management/${finalActivityId}`;
            } else if (actualActivityType === 'call-schedule') {
                apiUrl = `/call-schedule/${finalActivityId}`;
            } else if (actualActivityType === 'call-log' || actualActivityType === 'call') {
                apiUrl = `/call-logs/${finalActivityId}`;
            } else if (actualActivityType === 'meeting') {
                apiUrl = `/meetings/${finalActivityId}`;
            }

            if (apiUrl) {
                const response = await dispatch(getData(apiUrl));
                if (response && response.code === 200 && response.data) {
                    setActivity(response.data);
                    // Fetch related record (lead, contact, etc.)
                    if (response.data.refType && response.data.refId) {
                        fetchRelatedRecord(response.data.refType, response.data.refId);
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching activity details:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchRelatedRecord = async (refType: string, refId: string) => {
        try {
            let apiUrl = '';
            const refTypeLower = refType.toLowerCase();

            if (refTypeLower === 'leads') {
                apiUrl = `/leads/${refId}`;
            } else if (refTypeLower === 'contacts') {
                apiUrl = `/contacts/${refId}`;
            } else if (refTypeLower === 'businesses') {
                apiUrl = `/accounts/${refId}`;
            } else if (refTypeLower === 'deals') {
                apiUrl = `/deals/${refId}`;
            }

            if (apiUrl) {
                const response = await dispatch(getData(apiUrl));
                if (response && response.code === 200 && response.data) {
                    setRelatedRecord(response.data);
                }
            }
        } catch (error) {
            console.error('Error fetching related record:', error);
        }
    };

    const getActivityTitle = () => {
        if (!activity) return 'Activity Details';

        if (finalActivityType === 'task') {
            return `${activity.subject || 'Task'} - Tasks`;
        } else if (finalActivityType === 'call') {
            const isScheduled = actualActivityType === 'call-schedule' || (activity.scheduledDate && !activity.callDate);
            return `${activity.subject || 'Call'} - ${isScheduled ? 'Scheduled Call' : 'Call Log'}`;
        } else if (finalActivityType === 'meeting') {
            return `${activity.subject || 'Meeting'} - Meetings`;
        }
        return 'Activity Details';
    };

    const getRelatedRecordName = () => {
        if (!relatedRecord) return '-';

        if (relatedRecord.firstName || relatedRecord.lastName) {
            return [relatedRecord.preName, relatedRecord.firstName, relatedRecord.lastName].filter(Boolean).join(' ');
        } else if (relatedRecord.businessName) {
            return relatedRecord.businessName;
        } else if (relatedRecord.dealName) {
            return relatedRecord.dealName;
        }
        return '-';
    };

    const getStatusColor = (status: string, type?: string) => {
        if (type === 'call') {
            const statusColors: { [key: string]: string } = {
                'Completed': 'green',
                'Scheduled': 'blue',
                'No Answer': 'orange',
                'Busy': 'yellow',
                'Failed': 'red',
                'Missed': 'red',
                'Cancelled': 'default',
                'Rescheduled': 'cyan'
            };
            return statusColors[status] || 'default';
        } else if (type === 'task') {
            const statusColors: { [key: string]: string } = {
                'Completed': 'green',
                'In Progress': 'blue',
                'Not Started': 'default',
                'On Hold': 'orange',
                'Cancelled': 'red',
                'Deferred': 'yellow',
                'Waiting for Response': 'cyan'
            };
            return statusColors[status] || 'default';
        } else if (type === 'meeting') {
            const statusColors: { [key: string]: string } = {
                'Completed': 'green',
                'Scheduled': 'blue',
                'Cancelled': 'red',
                'Rescheduled': 'cyan'
            };
            return statusColors[status] || 'default';
        }
        return 'default';
    };

    const renderActivityDetails = () => {
        if (!activity) return <Empty description="No activity data" />;

        // Use existing detail components based on activity type
        if (finalActivityType === 'task') {
            // TasksDetails has its own full layout
            return <TasksDetails task={activity} />;
        } else if (finalActivityType === 'call') {
            // CallLogsDetails is a simple component, wrap it in a container
            return (
                <div style={{ background: '#f0f2f5', minHeight: '100vh', padding: '16px' }}>
                    <div style={{ background: '#FFFFFF', borderRadius: 4 }}>
                        <CallLogsDetails callLog={activity} />
                    </div>
                </div>
            );
        } else if (finalActivityType === 'meeting') {
            // For meetings, use a simple details view similar to CallLogsDetails
            return (
                <div style={{ background: '#f0f2f5', minHeight: '100vh', padding: '16px' }}>
                    <div style={{ background: '#FFFFFF', borderRadius: 4, padding: '24px' }}>
                        <Title level={4} style={{ marginBottom: 24 }}>
                            Meeting Details
                        </Title>
                        <Descriptions bordered column={2}>
                            <Descriptions.Item label="Subject" span={2}>
                                <Text strong>{activity.subject || 'N/A'}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Meeting Date">
                                <Space>
                                    <CalendarOutlined />
                                    <Text>
                                        {activity.meetingDate
                                            ? moment(activity.meetingDate).format('DD MMMM YYYY HH:mm')
                                            : 'N/A'}
                                    </Text>
                                </Space>
                            </Descriptions.Item>
                            <Descriptions.Item label="Meeting Status">
                                <Tag color={getStatusColor(activity.meetingStatus, 'meeting')}>
                                    {activity.meetingStatus || 'N/A'}
                                </Tag>
                            </Descriptions.Item>
                            {activity.duration && (
                                <Descriptions.Item label="Duration">
                                    {activity.duration} minutes
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label="Meeting Owner" span={2}>
                                {activity.meetingOwner ? (
                                    <SmallProfileCard
                                        name={constants.GET_FULL_NAME(activity.meetingOwner)}
                                        avatar={activity.meetingOwner?.avatar}
                                    />
                                ) : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Related To" span={2}>
                                {activity.refId ? (
                                    <>
                                        {['Leads', 'Contacts', 'User', 'ShortlistedCandidatesForInterview'].includes(
                                            activity.refType
                                        ) ? (
                                            <SmallProfileCard
                                                name={constants.GET_FULL_NAME(activity.refId)}
                                                avatar={activity.refId?.profilePhoto || activity.refId?.avatar}
                                            />
                                        ) : activity.refType === 'Businesses' ? (
                                            activity.refId?.businessName || 'N/A'
                                        ) : activity.refType === 'Deals' ? (
                                            activity.refId?.dealName || 'N/A'
                                        ) : (
                                            'N/A'
                                        )}
                                    </>
                                ) : 'N/A'}
                            </Descriptions.Item>
                            {activity.description && (
                                <Descriptions.Item label="Description" span={2}>
                                    <Text>{activity.description}</Text>
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label="Created At">
                                {activity.createdAt
                                    ? moment(activity.createdAt).format('DD MMMM YYYY HH:mm')
                                    : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Updated At">
                                {activity.updatedAt
                                    ? moment(activity.updatedAt).format('DD MMMM YYYY HH:mm')
                                    : 'N/A'}
                            </Descriptions.Item>
                        </Descriptions>
                    </div>
                </div>
            );
        }

        return <Empty description="Unknown activity type" />;
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!activity) {
        return (
            <div style={{ padding: '24px' }}>
                <Text>Activity not found</Text>
            </div>
        );
    }

    // TasksDetails has its own header and layout, so don't wrap it
    if (finalActivityType === 'task' && activity) {
        return <TasksDetails task={activity} />;
    }

    // For calls and meetings, render with header
    return (
        <div style={{ background: '#f0f2f5', minHeight: '100vh' }}>
            {/* Header */}
            <div style={{ background: '#FFFFFF', padding: '16px 24px', borderBottom: '1px solid #f0f0f0' }}>
                <Flex justify="space-between" align="center">
                    <Flex align="center" gap={16}>
                        <Button
                            type="text"
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate(-1)}
                        />
                        <Title level={4} style={{ margin: 0 }}>
                            {getActivityTitle()}
                        </Title>
                    </Flex>
                    <Space>
                        <Button type="primary" icon={<MoreOutlined />}>
                            Mark as Completed
                        </Button>
                        <Button type="text" icon={<MoreOutlined />} />
                    </Space>
                </Flex>
            </div>

            {/* Render activity details using built-in components */}
            {renderActivityDetails()}
        </div>
    );
};

export default ActivityDetails;
